/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { FocusEvent, Fragment, useEffect, useRef, useState } from 'react'
import { Listbox } from '@headlessui/react'
import clsx from 'clsx'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { countryList, PhoneCountry } from 'utils/countryUtils'
import {
    formatNationalPhoneAsYouType,
    stripNationalTrunkPrefix,
} from 'utils/phoneFormatUtils'
import { useTranslation } from 'react-i18next'
import { CheckIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import T from 'components/T'
import { Overlay } from 'react-overlays'
import useGetCountryCode from 'utils/hooks/useGetCountryCode'
import UiFormLabel from 'components/Ui/UiFormLabel'

interface PhoneWithCountrySelectCountryComponentProps {
    countryCode?: string
    style?: 'normal' | 'decorated'
}

const PhoneWithCountrySelectCountryComponent = (
    props: PhoneWithCountrySelectCountryComponentProps
) => {
    const [countryFlag, setCountryFlag] = useState(null)

    useEffect(() => {
        const loadCountryFlag = async () => {
            try {
                const countryFlagModule = await import(
                    `components/CountryIcons/3x2/${props.countryCode}.svg`
                )
                setCountryFlag(countryFlagModule.default)
            } catch (error) {
                console.error(
                    `Error loading country flag for ${props.countryCode}:`,
                    error
                )
                // Handle error (e.g., display a placeholder flag)
            }
        }

        if (props.countryCode) {
            loadCountryFlag()
        }

        // Clean up on unmount
        return () => {
            setCountryFlag(null)
        }
    }, [props.countryCode])

    return (
        // <div className={"CountryFlag min-w-[20px] w-[20px] min-h-[13px] h-[13px] border border-gray-200"}>
        <div
            className={clsx(
                'CountryFlag flex items-center justify-center overflow-hidden',
                props?.style === 'decorated' &&
                'decorated min-w-10 h-10 rounded-l-lg',
                !props?.style &&
                'not-decorated w-6 h-4 border border-gray-200 dark:border-gray-800 rounded-sm'
            )}
        >
            {countryFlag && (
                <img
                    className={clsx(
                        props?.style === 'decorated'
                            ? 'object-cover h-full w-full'
                            : 'object-cover h-full w-full'
                    )}
                    alt={`${props.countryCode} flag`}
                    src={countryFlag}
                />
            )}
        </div>
    )
}

interface ReservationDetailPhoneWithCountrySelectProps {
    options: PhoneCountry[]
    defaultCountry: PhoneCountry
    value: any
    register: any
    name: string
    label: string
    className?: string
    classNameInput?: string
    required?: boolean
    errors?: any
    disabled?: boolean
    onFocus?: (element: FocusEvent<HTMLInputElement>) => void
    setValue?: any
    keyboard?: any
    setSelectedCountry: (country: PhoneCountry) => void
    style?: 'normal' | 'inline'
    driverCountry?: string
    driverIsInvited?: boolean
    resetValue?: boolean
    setResetValue?: (val: boolean) => void
    setPhoneNumberWasSuggested?: (val: boolean) => void
}

const ReservationDetailPhoneWithCountrySelect = (
    props: ReservationDetailPhoneWithCountrySelectProps
) => {
    const { t } = useTranslation()
    const getCountryCode = useGetCountryCode()
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry | null>(
        props.defaultCountry || null
    )
    const containerRef = useRef<HTMLDivElement>(null)
    const targetRef = useRef(null)
    const [show, setShow] = useState(false)
    const options = props?.options
    const selectedOptionRef = useRef<HTMLLIElement>(null)
    const overlayPlacement =
        window.innerWidth >= 640 ? 'bottom-start' : 'top-start'

    const changePhoneNumberToCorrectFormat = async (originPhone: string) => {
        const isDoubleZero = originPhone.startsWith('00', 0)
        const isOneZero = originPhone.startsWith('0', 0)
        if (props?.driverCountry && props?.driverIsInvited) {
            if (isDoubleZero) {
                return '+' + originPhone.slice(2)
            }
            if (isOneZero) {
                const countryCode =
                    await getCountryCode.getCountryCodeFromCountryName(
                        props?.driverCountry
                    )
                const searchedCountryPhonePrefix = countryList.find(
                    (item) => item.textCode === countryCode
                )
                return searchedCountryPhonePrefix?.code
                    ? searchedCountryPhonePrefix.code + originPhone.slice(1)
                    : originPhone
            }
        }
        return originPhone
    }

    const searchCountryByLanguageCode = (countryCode: string): PhoneCountry => {
        const countryIndex = props.options.findIndex(
            (country) => country.textCode == countryCode
        )
        return props.options[countryIndex]
    }

    const searchCountryByCode = (code: string | null): PhoneCountry | null => {
        if (!code) {
            return null
        }
        const countryIndex = props.options.findIndex(
            (country) => country.textCode === code
        )
        return props.options[countryIndex]
    }

    const setSelectedCountryHandler = (country: PhoneCountry) => {
        setSelectedCountry(country)
        setShow(false)
        props.setSelectedCountry(country)
        props.setValue(country.code)
    }

    const suggestPhoneNumberCheck = async (currentPhone: string) => {
        const newSuggestPhone =
            await changePhoneNumberToCorrectFormat(currentPhone)
        if (newSuggestPhone !== undefined) {
            return newSuggestPhone
        }
        return currentPhone
    }

    const setPhoneNumber = () => {
        suggestPhoneNumberCheck(props.value).then((respPhone) => {
            let currentSelectedCountry: PhoneCountry | null = null
            try {
                const phoneNumber = parsePhoneNumber(respPhone)
                currentSelectedCountry = searchCountryByCode(
                    phoneNumber?.country ? phoneNumber.country : null
                )
            } catch {
                currentSelectedCountry = null
            }

            if (currentSelectedCountry) {
                setSelectedCountry(currentSelectedCountry)
                props.setSelectedCountry(currentSelectedCountry)
            } else {
                props.setSelectedCountry(props.defaultCountry)
                setSelectedCountry(props.defaultCountry)
            }

            // Do not rewrite the field value during typing; only suggest invited-driver normalization.
            if (respPhone && respPhone !== props.value) {
                props.setValue(respPhone)
            }

            props?.setPhoneNumberWasSuggested &&
                props.setPhoneNumberWasSuggested(
                    !!(props?.value && respPhone != props.value)
                )
        })
    }

    useEffect(() => {
        if (props?.value && props.value.length > 1) {
            setPhoneNumber()
        } else {
            const switzerlandCountry = searchCountryByCode('CH')
            const defaultCountry = switzerlandCountry || searchCountryByCode(
                props.defaultCountry?.textCode
            )
            if (defaultCountry) {
                setSelectedCountry(defaultCountry)
                if (defaultCountry.code && (!props.value || props.value === '')) {
                    props.setValue(defaultCountry.code)
                }
            }
        }
    }, [props?.value, props.defaultCountry])

    // console.log("props.value", props.value);
    useEffect(() => {
        if (props?.resetValue && props?.setResetValue) {
            props?.value
                ? setPhoneNumber()
                : setSelectedCountryHandler(
                    searchCountryByLanguageCode(
                        props.defaultCountry?.textCode
                    )
                )
            props.setResetValue(false)
        }
    }, [props?.resetValue])

    return (
        <div
            className={clsx(
                'ReservationDetailPhoneWithCountrySelect w-full relative',
                props?.style === 'inline' &&
                'sm:flex sm:flex-row sm:space-x-6 sm:items-center sm:flex-wrap',
                props.className
            )}
        >
            <UiFormLabel
                className={clsx(
                    'mb-1.5 flex',
                    props.errors?.[props.name]
                        ? 'text-red-500 dark:text-red-400'
                        : ''
                )}
                isRequired={props.required}
            >
                {t(props.label)}
            </UiFormLabel>

            <div
                ref={containerRef}
                className={clsx(
                    'ReservationDetailPhoneWithCountrySelectInner flex flex-row relative flex-1 gap-0',
                    props?.style === 'inline' && 'sm:w-2/3! sm:flex-none!'
                )}
            >
                <Listbox
                    disabled={props.disabled}
                    value={selectedCountry as any}
                    onChange={setSelectedCountryHandler}
                >
                    {({ open }) => (
                        <div className="w-auto">
                            <Listbox.Button
                                ref={targetRef}
                                className={clsx(
                                    'relative h-10 flex items-center justify-between w-full rounded-l-lg border border-gray-300 dark:border-gray-800 text-sm font-medium transition-colors border-r-0 shadow-sm',
                                    props.disabled
                                        ? 'bg-gray-100 dark:bg-gray-900 text-gray-500 cursor-not-allowed'
                                        : 'bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100',
                                    props.errors?.[props.name] &&
                                    !open &&
                                    !selectedCountry &&
                                    'border-red-500 focus:border-red-500'
                                )}
                                onClick={() => setShow(!show)}
                            >
                                <div className="flex items-center">
                                    <PhoneWithCountrySelectCountryComponent
                                        countryCode={selectedCountry?.textCode}
                                        style={'decorated'}
                                    />
                                    {selectedCountry?.code && (
                                        <span className="px-1 text-sm font-semibold text-gray-600 dark:text-gray-400 border-l border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 h-10 flex items-center">
                                            {selectedCountry.code}
                                        </span>
                                    )}
                                </div>
                            </Listbox.Button>
                            <Overlay
                                placement={overlayPlacement}
                                show={show}
                                target={targetRef.current}
                                rootClose
                                onHide={() => setShow(false)}
                            >
                                {({ props }) => (
                                    <Listbox.Options
                                        {...props}
                                        style={{
                                            ...props.style,
                                            transition:
                                                'maxHeight 0.3s ease-in-out',
                                            // @ts-ignore
                                            width: containerRef?.current
                                                ? containerRef.current.getBoundingClientRect()
                                                    .width
                                                : '100%',
                                        }}
                                        className={
                                            'transition-all duration-300 ease-in h-auto shadow-xl z-9999 max-h-60 w-full overflow-auto rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 py-1 text-base'
                                        }
                                        unmount={false}
                                        onLoad={() => {
                                            if (
                                                selectedCountry &&
                                                selectedOptionRef.current
                                            ) {
                                                selectedOptionRef.current.scrollIntoView(
                                                    {
                                                        behavior: 'smooth',
                                                        block: 'nearest',
                                                    }
                                                )
                                            }
                                        }}
                                    >
                                        {options.map((option) => (
                                            <Listbox.Option
                                                key={option.textCode}
                                                as={Fragment}
                                                value={option}
                                            >
                                                {({ selected, active }) => (
                                                    <li
                                                        ref={
                                                            selected
                                                                ? selectedOptionRef
                                                                : null
                                                        }
                                                        className={clsx(
                                                            'relative flex flex-row items-center cursor-pointer select-none py-2 pl-9 pr-4 text-sm transition-colors',
                                                            selected
                                                                ? 'bg-gray-100 dark:bg-gray-800 font-semibold text-gray-900 dark:text-gray-100'
                                                                : active
                                                                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                                                                    : 'text-gray-900 dark:text-gray-100'
                                                        )}
                                                    >
                                                        {selected && (
                                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600 dark:text-primary-400">
                                                                <CheckIcon
                                                                    className="w-4 h-4"
                                                                    aria-hidden="true"
                                                                />
                                                            </span>
                                                        )}
                                                        <div className="flex items-center truncate">
                                                            <PhoneWithCountrySelectCountryComponent
                                                                countryCode={
                                                                    option.textCode
                                                                }
                                                            />
                                                            <span
                                                                className={clsx(
                                                                    'ml-2 block truncate'
                                                                )}
                                                            >
                                                                {option.code}{' '}
                                                                {option.name}
                                                            </span>
                                                        </div>
                                                    </li>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                )}
                            </Overlay>
                        </div>
                    )}
                </Listbox>
                <input
                    autoComplete={'tel'}
                    className={clsx(
                        'block w-full h-10 px-1 py-2 rounded-r-lg border border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 text-sm md:text-sm font-medium transition-all placeholder:text-gray-500 shadow-sm focus:outline-none focus:border-primary-600',
                        props.classNameInput,
                        props.errors?.[props.name]
                            ? 'border-red-500 focus:border-red-500'
                            : 'focus:border-cta-light',
                        props.disabled &&
                        'bg-gray-100 dark:bg-gray-900 text-gray-500 cursor-not-allowed opacity-50'
                    )}
                    value={(() => {
                        const val = props.value || ''
                        const prefix = selectedCountry?.code || ''
                        if (prefix && val.startsWith(prefix)) {
                            const nationalNumber = val
                                .slice(prefix.length)
                                .replace(/\s/g, '')
                            return formatNationalPhoneAsYouType(
                                nationalNumber,
                                selectedCountry?.textCode as CountryCode,
                                prefix
                            )
                        }
                        return val
                    })()}
                    disabled={props.disabled}
                    name={props.name}
                    required={props.required}
                    type={'tel'}
                    onFocus={props.onFocus}
                    onChange={(e) => {
                        const inputVal = e.target.value.replace(/[^\d\s]/g, '') // Keep digits and spaces
                        const rawNumber = inputVal.replace(/\s/g, '') // Strip spaces for storage
                        const prefix = selectedCountry?.code || ''
                        const nationalNumber = stripNationalTrunkPrefix(
                            rawNumber,
                            selectedCountry?.textCode as CountryCode,
                            prefix
                        )

                        // Propagate full number with prefix to the form (no trunk 0)
                        props.setValue(prefix + nationalNumber)
                    }}
                />
            </div>
            {props.errors?.[props.name]?.message && (
                <div
                    className={clsx(
                        'text-red-500 text-xs mt-1 flex flex-row items-center flex-initial'
                    )}
                >
                    <div
                        className={clsx(
                            'flex-initial flex flex-row items-center w-full'
                        )}
                    >
                        <ExclamationCircleIcon
                            className={
                                'size-4 flex-none mr-0.5'
                            }
                        />
                        <T>{props.errors?.[props.name]?.message}</T>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ReservationDetailPhoneWithCountrySelect
